import { StructureObjectDto } from '@/rtkApi/mddApiSliceGenerated'
import { SourceItem } from '@/rtkApi/mddApiSliceGeneratedV2'

type SourceItemStructureRef = Pick<
	SourceItem,
	'objectUuid' | 'objectId' | 'objectCode' | 'objectType'
>

const mapObjectTypeToStructureType = (
	objectType: SourceItem['objectType'],
): StructureObjectDto['type'] | undefined => {
	switch (objectType) {
		case 'TABLE':
			return 'TABLE'
		case 'VIEW':
			return 'VIEW'
		case 'DUMMY':
			return 'TABLE'
		default:
			return undefined
	}
}

const matchesExpectedType = (
	structure: StructureObjectDto,
	expectedType: StructureObjectDto['type'] | undefined,
) => {
	return !expectedType || structure.type === expectedType
}

const isReferenceOrHistoryRow = (
	structure: StructureObjectDto,
	group: StructureObjectDto[],
) =>
	group.some(
		(other) =>
			other !== structure &&
			!!other.fullPath &&
			!!structure.fullPath &&
			structure.fullPath.startsWith(`${other.fullPath} > `),
	)

const pickStructure = (
	matches: StructureObjectDto[],
	sourceItem: SourceItemStructureRef,
): StructureObjectDto | undefined => {
	const baseTables = matches.filter(
		(structure) => !isReferenceOrHistoryRow(structure, matches),
	)
	const pool = baseTables.length > 0 ? baseTables : matches

	if (sourceItem.objectCode) {
		const withCode = pool.filter(
			(structure) => structure.code === sourceItem.objectCode,
		)
		if (withCode.length === 1) return withCode[0]
	}

	return pool.length === 1 ? pool[0] : undefined
}

export const findStructureForSourceItem = (
	sourceItem: SourceItemStructureRef,
	structures: StructureObjectDto[],
): StructureObjectDto | undefined => {
	const expectedType = mapObjectTypeToStructureType(sourceItem.objectType)
	const typedStructures = structures.filter((structure) =>
		matchesExpectedType(structure, expectedType),
	)

	if (sourceItem.objectId != null) {
		const byId = typedStructures.filter(
			(structure) => structure.structureId === sourceItem.objectId,
		)
		if (byId.length > 0) {
			return pickStructure(byId, sourceItem)
		}
	}

	if (sourceItem.objectUuid) {
		const byUuid = typedStructures.filter(
			(structure) => structure.structureUuid === sourceItem.objectUuid,
		)
		if (byUuid.length === 0) return undefined
		return pickStructure(byUuid, sourceItem)
	}

	if (!sourceItem.objectCode || !expectedType) {
		return undefined
	}

	const structuresWithMatchingCode = structures.filter(
		(structure) =>
			structure.code === sourceItem.objectCode &&
			structure.type === expectedType,
	)

	return structuresWithMatchingCode.length === 1
		? structuresWithMatchingCode[0]
		: undefined
}
